import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { encountersAPI, userAPI, likesAPI } from '../services/api';
import { getProfilePicture, getUploadUrl, isOnline, formatDate } from '../utils/helpers';
import { FaTimes, FaGift, FaHeart, FaMapMarkerAlt, FaStar, FaInfoCircle, FaArrowLeft, FaUser, FaShieldAlt, FaCoins, FaCrown, FaCheckCircle, FaCheck, FaLock, FaComments, FaComment, FaInstagram, FaFacebook, FaTelegram, FaClock, FaPlus, FaImages, FaEnvelope, FaCamera, FaBell, FaCog, FaSignOutAlt } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useFriend } from '../context/FriendContext';
import { toast } from 'react-toastify';
import GiftModal from '../components/GiftModal';

// Gift emoji mapping - updated to match new database gift_catalog order
const GIFT_EMOJIS = {
  // ID mappings (1-26 matching new database order by category)
  1: '🌹',   // Rose (romantic)
  2: '❤️',   // Heart (romantic)
  3: '🍷',   // Wine Bottle (romantic)
  4: '💍',   // Diamond Ring (premium)
  5: '👑',   // Crown (premium)
  6: '💍',   // Ring (premium)
  7: '🛥️',  // Yacht (premium)
  8: '🧴',   // Perfume (luxury)
  9: '🚗',   // Car (luxury)
  10: '🏰',  // Castle (luxury)
  11: '🧸',  // Teddy Bear (cute)
  12: '🍫',  // Chocolate Box (sweet)
  13: '🍦',  // Ice Cream (sweet)
  14: '🍕',  // Pizza (food)
  15: '🍔',  // Burger (food)
  16: '☕',  // Coffee (daily)
  17: '🍺',  // Beer (drink)
  18: '🍸',  // Cocktail (drink)
  19: '🎂',  // Cake (celebration)
  20: '🍾',  // Champagne (celebration)
  21: '🎆',  // Fireworks (celebration)
  22: '🎈',  // Balloon (fun)
  23: '🎉',  // Confetti (fun)
  24: '💄',  // Lipstick (beauty)
  25: '👠',  // Shoes (fashion)
  26: '✈️',  // Plane (travel)

  // Name mappings (exact match with database names)
  'Rose': '🌹',
  'Heart': '❤️',
  'Wine Bottle': '🍷',
  'Diamond Ring': '💍',
  'Crown': '👑',
  'Ring': '💍',
  'Yacht': '🛥️',
  'Perfume': '🧴',
  'Car': '🚗',
  'Castle': '🏰',
  'Teddy Bear': '🧸',
  'Chocolate Box': '🍫',
  'Ice Cream': '🍦',
  'Pizza': '🍕',
  'Burger': '🍔',
  'Coffee': '☕',
  'Beer': '🍺',
  'Cocktail': '🍸',
  'Cake': '🎂',
  'Champagne': '🍾',
  'Fireworks': '🎆',
  'Balloon': '🎈',
  'Confetti': '🎉',
  'Lipstick': '💄',
  'Shoes': '👠',
  'Plane': '✈️'
};

const getGiftEmoji = (giftId, giftName) => {
  // First try exact match with database name
  if (giftName && typeof giftName === 'string' && giftName.trim()) {
    if (GIFT_EMOJIS[giftName.trim()]) {
      return GIFT_EMOJIS[giftName.trim()];
    }
    // Then try case-insensitive match
    const nameKey = giftName.toLowerCase().trim();
    if (GIFT_EMOJIS[nameKey]) {
      return GIFT_EMOJIS[nameKey];
    }
  }

  // Then try to match by ID
  if (giftId && GIFT_EMOJIS[giftId]) {
    return GIFT_EMOJIS[giftId];
  }

  // Default fallback
  return '🎁';
};

const ProfileDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout: authLogout } = useAuth();



  const [profile, setProfile] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [normalPhotos, setNormalPhotos] = useState([]);
  const [privatePhotos, setPrivatePhotos] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [profileFriends, setProfileFriends] = useState([]); // Friends of the profile owner
  const { friends, sendFriendRequest: contextSendFriendRequest, removeFriend: contextRemoveFriend, getFriendStatus, setFriendStatus, friendStatuses } = useFriend();

  const logout = async () => {
    try {
      await authLogout();
      navigate('/login');
    } catch (error) {
      console.error('ProfileDetail: Logout error:', error);
    }
  };

  // Local friend status state for this profile
  const [friendStatus, setLocalFriendStatus] = useState('none');
  const [viewerLiked, setViewerLiked] = useState(false);
  const [totalLikes, setTotalLikes] = useState(0);
  const [visitors, setVisitors] = useState([]);
  const [totalVisitors, setTotalVisitors] = useState(0);
  const [loading, setLoading] = useState(true);
  const [requestLoading, setRequestLoading] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [activeTab, setActiveTab] = useState('about');
  const [showPrivatePhotoModal, setShowPrivatePhotoModal] = useState(false);
  const [showPublicPhotoModal, setShowPublicPhotoModal] = useState(false);
  const [selectedPrivatePhoto, setSelectedPrivatePhoto] = useState(null);
  const [selectedPublicPhoto, setSelectedPublicPhoto] = useState(null);
  const [photoAccessRequests, setPhotoAccessRequests] = useState({});
  const [photoAccessRequestsStatus, setPhotoAccessRequestsStatus] = useState({}); // Store actual status from backend
  const [visitorsLoaded, setVisitorsLoaded] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  useEffect(() => {
    // Check if user is authenticated
    if (!user) {
      navigate('/login');
      return;
    }

    // If viewing own profile, redirect to Profile page
    if (id && parseInt(id) === user.id) {
      navigate(`/user/${user.id}`, { replace: true });
      return;
    }

    if (id) {
      fetchProfile();
    }
  }, [id, user, navigate]);

  // Listen for photo access revoke events
  useEffect(() => {
    const handlePhotoAccessRevoked = (event) => {
      const { profileId } = event.detail;

      if (parseInt(id) === profileId) {
        // Clear photo access for this profile
        const statusMap = {};
        setPhotoAccessRequestsStatus(statusMap);
      }
    };

    window.addEventListener('photoAccessRevoked', handlePhotoAccessRevoked);

    return () => {
      window.removeEventListener('photoAccessRevoked', handlePhotoAccessRevoked);
    };
  }, [id]);

  // Lazy load visitors when visitors tab is clicked
  const loadVisitors = async () => {
    if (visitorsLoaded) return;

    try {
      const visitorsResponse = await userAPI.getVisitors(id);
      if (visitorsResponse && visitorsResponse.visitors) {
        setVisitors(visitorsResponse.visitors);
        setTotalVisitors(visitorsResponse.totalVisitors || 0);
      } else if (Array.isArray(visitorsResponse)) {
        setVisitors(visitorsResponse);
      }
      setVisitorsLoaded(true);
    } catch (error) {
      console.error('Failed to load visitors:', error);
      setVisitorsLoaded(true); // Prevent retry
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const viewerId = user.id;
      const profileResponse = await userAPI.getProfile(id);

      if (!profileResponse?.success) {
        throw new Error('Invalid API response format');
      }

      const profileData = profileResponse.profile;
      const giftsData = profileResponse.gifts || [];
      const friendsData = profileResponse.friends || [];
      // Check FriendContext first, then fallback to API
      const contextFriendStatus = friendStatuses.get(profileData.id);
      const friendStatusData = contextFriendStatus || profileResponse.friend_status || 'none';
      // Filter out profile photos from normal photos
      const normalPhotosData = (profileResponse.profile.normal_photos || []).filter(photo => photo.photo_type !== 'profile');
      const privatePhotosData = profileResponse.profile.private_photos || [];
      const viewerLikedData = profileResponse.viewer_liked || false;
      const totalLikesData = profileResponse.total_likes || 0;


      if (!profileData) {
        throw new Error('Profile data not found');
      }

      // Get photos separately
      let photosData = [];
      try {
        const photosResponse = await userAPI.getPhotos(id);
        if (photosResponse) {
          photosData = Array.isArray(photosResponse) ? photosResponse : (photosResponse.photos || []);
        }
      } catch (photosError) {
        console.warn('Photos failed:', photosError.message);
      }

      // Visitors will be loaded lazily when visitors tab is clicked

      setProfile(profileData);
      setGifts(giftsData);
      // Set local friend status
      setLocalFriendStatus(friendStatusData);
      // Also save to global context
      setFriendStatus(profileData.id, friendStatusData);
      // If API doesn't return normal/private photos, derive them from all photos
      let finalNormalPhotos = normalPhotosData;
      let finalPrivatePhotos = privatePhotosData;

      if (normalPhotosData.length === 0 && privatePhotosData.length === 0 && photosData.length > 0) {
        finalNormalPhotos = photosData.filter(photo => photo.photo_type !== 'private' && photo.photo_type !== 'profile');
        finalPrivatePhotos = photosData.filter(photo => photo.photo_type === 'private');
      }


      setPhotos(photosData);
      setNormalPhotos(finalNormalPhotos);
      setPrivatePhotos(finalPrivatePhotos);
      setGifts(giftsData);
      setProfileFriends(friendsData); // Set profile owner's friends
      setViewerLiked(viewerLikedData);
      setTotalLikes(totalLikesData);
      // Visitors are loaded lazily when friends tab is clicked

      // Fetch photo access request statuses for this profile
      try {

        // 1. Profil sahibi her zaman kendi fotoğraflarını görebilir
        if (viewerId === parseInt(id)) {
          const statusMap = {};
          privatePhotosData.forEach(photo => {
            statusMap[photo.id] = 'approved'; // Profil sahibi tüm fotoğraflarını görebilir
          });
          setPhotoAccessRequestsStatus(statusMap);
        } else {
          // 2. Server'dan onay bilgisini kontrol et
          const statusMap = {};

          try {
            const approvedResponse = await userAPI.getApprovedPhotoAccess();
            console.log('🔍 ProfileDetail - Approved access check:', {
              profileId: id,
              viewerId,
              response: approvedResponse,
              hasApproval: approvedResponse.success && approvedResponse.approved_users ? approvedResponse.approved_users.includes(parseInt(id)) : false
            });
            if (approvedResponse.success && approvedResponse.approved_users) {
              const hasApproval = approvedResponse.approved_users.includes(parseInt(id));
              console.log('🔍 ProfileDetail - Access granted for profile:', id, 'hasApproval:', hasApproval);
              if (hasApproval) {
                console.log('🔍 ProfileDetail - Setting all private photos to approved for profile:', id);
                // Bu profile ait tüm private fotoğraflar için erişim ver
            privatePhotosData.forEach(photo => {
              statusMap[photo.id] = 'approved';
            });
          }
            }
          } catch (serverError) {
            console.error('Failed to check photo approvals from server:', serverError);
            // Fallback to localStorage if server fails
            const storageKey = `approvedPhotoRequests_user_${viewerId}`;
            const approvedRequests = JSON.parse(localStorage.getItem(storageKey) || '{}');
            const hasApproval = approvedRequests[parseInt(id)] || approvedRequests[id] || approvedRequests[String(id)];

            if (hasApproval) {
              privatePhotosData.forEach(photo => {
                statusMap[photo.id] = 'approved';
              });
            }
          }

          setPhotoAccessRequestsStatus(statusMap);
        }
      } catch (error) {
        console.error('❌ Failed to fetch photo access request statuses:', error);
        // Don't show error toast for this, it's not critical
      }

    } catch (error) {
      console.error('ProfileDetail: Error:', error.message);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!profile) {
      console.error('No profile data available');
      toast.error('Profile data not loaded');
      return;
    }

    if (!profile.id) {
      console.error('Profile ID is missing:', profile);
      toast.error('Profile ID is missing');
      return;
    }


    try {
      if (viewerLiked) {
        // Unlike
        await likesAPI.unlikeProfile(profile.id);
        toast.success('Profile unliked!');
        setViewerLiked(false);
        setTotalLikes(prev => Math.max(0, prev - 1));
      } else {
        // Like
        await likesAPI.likeProfile(profile.id);
        toast.success('Profile liked!');
        setViewerLiked(true);
        setTotalLikes(prev => prev + 1);
      }
    } catch (error) {
      console.error('Like/Unlike error:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);

      const errorMessage = error.response?.data?.message ||
                          (viewerLiked ? 'Failed to unlike profile' : 'Failed to like profile');
      toast.error(errorMessage);
    }
  };

  const getFirstName = (fullName) => {
    return fullName ? fullName.split(' ')[0] : 'User';
  };

  const handleFriendAction = async () => {
    if (!profile) return;

    try {
      if (friendStatus === 'none') {
        // Send friend request using FriendContext
        const success = await contextSendFriendRequest(profile.id, getFirstName(profile.full_name));
        if (success) {
          setLocalFriendStatus('pending');
          setFriendStatus(profile.id, 'pending');
        }
      } else if (friendStatus === 'accepted') {
        // Remove friend using FriendContext
        const success = await contextRemoveFriend(profile.id, getFirstName(profile.full_name));
        if (success) {
          setLocalFriendStatus('none');
          setFriendStatus(profile.id, 'none');
        }
      }
    } catch (error) {
      console.error('Failed to handle friend action:', error);
      toast.error('Failed to handle friend action');
    }
  };

  const handleDislike = async () => {
    try {
      await encountersAPI.dislike(profile.id);
      toast.success('Profile passed');
      navigate('/encounters');
    } catch (error) {
      toast.error('Failed to pass profile');
    }
  };

  const handlePrivatePhotoRequest = async (photoId) => {
    try {
      // API çağrısı burada yapılacak - şimdilik toast gösterelim
      toast.success('Photo access request sent!');
      setPhotoAccessRequests(prev => ({
        ...prev,
        [photoId]: 'pending'
      }));
    } catch (error) {
      toast.error('Failed to send request');
    }
  };

  const openPrivatePhotoModal = (photo) => {
    setSelectedPrivatePhoto(photo);
    setShowPrivatePhotoModal(true);
  };

  const openPublicPhotoModal = (photo) => {
    setSelectedPublicPhoto(photo);
    setShowPublicPhotoModal(true);
  };

  const handleSendMessageRequest = async (target) => {
    if (requestLoading) return; // Prevent duplicate requests

    try {
      setRequestLoading(true);
      if (target === 'all') {
        // Tüm private photo'lar için TEK BİR bulk request gönder
        const photoIds = privatePhotos.map(photo => photo.id);

        const bulkResult = await userAPI.requestBulkPhotoAccess(photoIds);

        const successfulRequests = bulkResult.successful || [];

        if (successfulRequests.length > 0) {
          toast.success(`Photo access request sent! Check your messages to see the conversation.`);

            // Photo request message is sent by backend as system message (is_photo_request = 1)

          // Refresh photo access request statuses (server'dan)
          try {
              const statusMap = {};
            const approvedResponse = await userAPI.getApprovedPhotoAccess();
            if (approvedResponse.success && approvedResponse.approved_users) {
              const hasApproval = approvedResponse.approved_users.includes(parseInt(id));
              if (hasApproval) {
                privatePhotos.forEach(photo => {
                  statusMap[photo.id] = 'approved';
                });
              }
            }
              setPhotoAccessRequestsStatus(statusMap);
          } catch (refreshError) {
            console.error('Failed to refresh photo access statuses:', refreshError);
          }

          setPhotoAccessRequests(prev => ({
            ...prev,
            [target]: 'message_sent'
          }));
        } else {
          toast.error('Failed to send photo access request');
        }
      } else {
        // Tek photo için request
        await userAPI.requestPhotoAccess(target);
        toast.success('Photo access request sent! Check your messages.');

        // Refresh photo access request statuses (localStorage'dan)
        try {
            const statusMap = {};
          const storageKey = `approvedPhotoRequests_user_${viewerId}`;
          const approvedRequests = JSON.parse(localStorage.getItem(storageKey) || '{}');
          if (approvedRequests[parseInt(id)]) {
            privatePhotos.forEach(photo => {
              statusMap[photo.id] = 'approved';
            });
          }
            setPhotoAccessRequestsStatus(statusMap);
        } catch (refreshError) {
          console.error('Failed to refresh photo access statuses:', refreshError);
        }

        setPhotoAccessRequests(prev => ({
          ...prev,
          [target]: 'message_sent'
        }));
      }
    } catch (error) {
      console.error('Photo access request error:', error);
      toast.error('Failed to send photo access request');
    } finally {
      setRequestLoading(false);
    }
  };

  const handleRevokeAccess = async () => {
    try {
      // Tüm approved request'leri revoke et
      const revokePromises = Object.entries(photoAccessRequestsStatus)
        .filter(([_, status]) => status === 'approved')
        .map(async ([photoId, _]) => {
          try {
            // Find the request ID for this photo
            const accessRequestsResponse = await userAPI.getPhotoAccessRequests();
            let request = null;

            // Check sent requests
            if (accessRequestsResponse.sent_requests) {
              request = accessRequestsResponse.sent_requests.find(
              req => req.photo_id === parseInt(photoId) && req.owner_id === parseInt(id)
            );
            }

            // Also check received requests
            if (!request && accessRequestsResponse.received_requests) {
              request = accessRequestsResponse.received_requests.find(
                req => req.photo_id === parseInt(photoId) && req.owner_id === parseInt(id)
              );
            }

            if (request) {
              await userAPI.respondToPhotoAccess(request.id, 'revoke');
            }
            return photoId;
          } catch (error) {
            console.error(`Failed to revoke access for photo ${photoId}:`, error);
            return null;
          }
        });

      const results = await Promise.all(revokePromises);
      const successfulRevokes = results.filter(id => id !== null);

      if (successfulRevokes.length > 0) {
        toast.success('Photo access revoked successfully');

        // Send revoke message to the other user
        try {
          await messagesAPI.sendMessage({
            receiver_id: parseInt(id),
            message: 'I\'ve revoked your access to view my private photos.',
          });
        } catch (messageError) {
          console.error('Failed to send revoke message:', messageError);
          // Don't show error for this, it's not critical
        }

        // Refresh photo access request statuses (server'dan tekrar çek)
        try {
          const statusMap = {};
          // Server'dan güncel durumu tekrar çek
          const approvedResponse = await userAPI.getApprovedPhotoAccess();
          if (approvedResponse.success && approvedResponse.approved_users) {
            const hasApproval = approvedResponse.approved_users.includes(parseInt(id));
            if (hasApproval) {
              privatePhotos.forEach(photo => {
                statusMap[photo.id] = 'approved';
              });
            }
          }
            setPhotoAccessRequestsStatus(statusMap);
        } catch (refreshError) {
          console.error('Failed to refresh photo access statuses:', refreshError);
          // Clear all access on error
          setPhotoAccessRequestsStatus({});
        }
      } else {
        toast.error('Failed to revoke photo access');
      }
    } catch (error) {
      console.error('Revoke access error:', error);
      toast.error('Failed to revoke photo access');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="matchus-spinner mx-auto mb-4"></div>
          <h2>Loading Profile...</h2>
          <p>Please wait while we load the profile</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="matchus-spinner mx-auto mb-4"></div>
          <h2>Profile Not Found</h2>
          <p>The profile you're looking for doesn't exist.</p>
          <Link to="/encounters" className="matchus-retry-btn">
            Back to Encounters
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Enhanced Header */}
      <header className="matchus-encounters-header">
        <div className="matchus-header-container">
          <div className="matchus-header-left">
            <Link to="/encounters" className="matchus-profile-back-btn">
              <FaArrowLeft className="text-xl" />
              <span className="hidden sm:inline ml-2">Back</span>
            </Link>
          </div>
          <div className="matchus-header-center">
            <h1 className="matchus-page-title">Profile Detail</h1>
          </div>
          <div className="matchus-header-right">
            <button className="matchus-header-btn" title="Notifications">
              <FaBell />
              <span className="matchus-notification-badge">2</span>
            </button>

            <div className="matchus-user-menu">
              <button
                className="matchus-user-btn"
                onClick={() => setShowUserDropdown(!showUserDropdown)}
              >
                <img
                  key={user?.id || 'guest'}
                  src={user?.profile_photo ? getUploadUrl(`user-${user.id}/${user.profile_photo}`) : getProfilePicture(user || { id: 1 })}
                  alt="Profile"
                  className="matchus-user-avatar"
                />
                <span className="matchus-user-name">{user?.full_name?.split(' ')[0] || 'You'}</span>
              </button>

              <div className={`matchus-user-dropdown ${showUserDropdown ? 'show' : ''}`}>
                <Link to="/settings" className="matchus-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                  <FaCog />
                  <span>Settings</span>
                </Link>
                <Link to={`/user/${user?.id}`} className="matchus-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                  <FaUser />
                  <span>Profile</span>
                </Link>
                <div className="matchus-dropdown-divider"></div>
                {(user?.is_admin === 1 || user?.role?.name === 'admin' || user?.role?.name === 'moderator') && (
                  <Link to="/admin" className="matchus-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                    <FaCog />
                    <span>Admin Panel</span>
                  </Link>
                )}
                <button
                  type="button"
                  className="matchus-dropdown-logout"
                  onClick={() => {
                    setShowUserDropdown(false);
                    logout(() => navigate('/?login'));
                  }}
                >
                  <FaSignOutAlt />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Overlay to close dropdown */}
        {showUserDropdown && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowUserDropdown(false)}
          />
        )}
      </header>

      {/* Main Content with Enhanced Styling */}
      <div className="matchus-main-content">
        <div className="matchus-profile-content-wrapper">
          {/* Enhanced Profile Header */}
          <div className="matchus-profile-hero">
            <div className="matchus-profile-hero-card">
              <div className="matchus-profile-hero-image">
                <img
                  src={getProfilePicture(profile)}
                  alt={profile.full_name}
                  className="matchus-profile-main-photo"
                />

                {/* VIP Badge - Sol üst, sarı */}
                {profile.is_vip === true && (
                  <div className="matchus-vip-badge">
                    <FaCrown className="text-yellow-700 mr-1" />
                    <span>VIP</span>
                  </div>
                )}

                {/* Verified Badge - Sağ üst, yeşil */}
                {profile.is_verified === true && (
                  <div className="matchus-verified-badge">
                    <FaShieldAlt className="text-white" />
                  </div>
                )}

                {/* New Member Badge - Sağ alt, pembe */}
                {profile.is_new === true && (
                  <div className="matchus-new-member-badge">
                    <span>NEW</span>
                  </div>
                )}

                {/* Popular Badge - eski */}
                {profile.is_popular === true && (
                  <div className="matchus-popular-badge">
                    <FaStar className="text-yellow-400" />
                    <span>POPULAR</span>
                  </div>
                )}
              </div>

              <div className="matchus-profile-hero-info">
                <div className="matchus-profile-name-section">
                  <h1 className="matchus-profile-display-name">{profile.full_name}</h1>

                  <div className="matchus-profile-details-stack">
                    <div className="matchus-profile-detail-item">
                      <span className="matchus-profile-age">{profile.age} years old</span>
                    </div>

                    <div className="matchus-profile-detail-item">
                      <span className="matchus-profile-location">
                        <FaMapMarkerAlt className="text-gray-400 mr-1" />
                        {profile.city}{profile.city && profile.country && ', '}{profile.country}
                      </span>
                    </div>

                    <div className="matchus-profile-detail-item">
                      <div className={`matchus-online-indicator ${profile.isOnline ? 'online' : 'offline'}`}>
                        <div className="matchus-online-dot"></div>
                        <span className="ml-2 text-sm font-medium">
                          {profile.isOnline ? 'Online' : 'Offline'}
                        </span>
                      </div>
                      {/* Debug online status removed */}
                    </div>
                  </div>
                </div>

                {/* Match Score in Hero */}
                <div className="matchus-profile-match-score">
                  <div className="matchus-match-stats">
                    <div className="matchus-match-stat">
                      <div className="matchus-match-circle-small">
                        <span className="matchus-match-percentage-small">{profile.score?.percentage || 85}%</span>
                      </div>
                      <span className="matchus-match-text">Match</span>
                    </div>
                    <div className="matchus-match-stat">
                      <span className="matchus-match-number">{totalLikes}</span>
                      <span className="matchus-match-text">Total Likes</span>
                    </div>
                    <div className="matchus-match-stat">
                      <span className="matchus-match-number">{totalVisitors}</span>
                      <span className="matchus-match-text">Visitors</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons - Like Encounters */}
          <div className="matchus-buttons-row">
            <div className="matchus-actions-card-bottom">
              <button
                className="matchus-action-btn nope"
                onClick={handleDislike}
                title="Pass"
              >
                <FaTimes />
              </button>
              <Link
                to={`/messages?chat=${profile.id}&direct=true`}
                className="matchus-action-btn message"
                title={`Send Message to ${profile.full_name}`}
                onClick={() => console.log('💬 ProfileDetail: Opening chat with user ID:', profile.id, 'Name:', profile.full_name)}
              >
                <FaComment />
              </Link>
              <button
                className="matchus-action-btn gift"
                onClick={() => setShowGiftModal(true)}
                title="Send Gift"
              >
                <FaGift />
              </button>
              <button
                className={`matchus-action-btn friend ${friendStatus === 'accepted' ? 'friends' : friendStatus === 'pending' ? 'pending' : ''}`}
                onClick={handleFriendAction}
                disabled={friendStatus === 'pending'}
                title={friendStatus === 'accepted' ? 'Friends' :
                       friendStatus === 'pending' ? 'Request Sent' :
                       friendStatus === 'pending_received' ? 'Friend Request Received' :
                       'Add Friend'}
              >
                {friendStatus === 'pending' ? <FaClock /> :
                 friendStatus === 'pending_received' ? <FaEnvelope /> :
                 friendStatus === 'accepted' ? <FaUser /> :
                 <>
                   <FaUser />
                   <FaPlus />
                 </>}
              </button>
              <button
                className={`matchus-action-btn like ${viewerLiked ? 'liked' : ''}`}
                onClick={handleLike}
                title={viewerLiked ? 'Unlike' : 'Like'}
              >
                <FaHeart />
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="matchus-profile-tabs">
            <button
              className={`matchus-tab-btn ${activeTab === 'about' ? 'active' : ''}`}
              onClick={() => setActiveTab('about')}
            >
              <FaInfoCircle className="mr-2" />
              About
            </button>
            <button
              className={`matchus-tab-btn ${activeTab === 'photos' ? 'active' : ''}`}
              onClick={() => setActiveTab('photos')}
            >
              <FaImages className="mr-2" />
              Photos ({normalPhotos.length + privatePhotos.length})
            </button>
            <button
              className={`matchus-tab-btn ${activeTab === 'gifts' ? 'active' : ''}`}
              onClick={() => setActiveTab('gifts')}
            >
              <FaGift className="mr-2" />
              Gifts ({gifts.length})
            </button>
            <button
              className={`matchus-tab-btn ${activeTab === 'friends' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('friends');
                loadVisitors(); // Load visitors when friends tab is clicked
              }}
            >
              <FaHeart className="mr-2" />
              Friends ({profileFriends.length})
            </button>
          </div>

          {/* Tab Content */}
          <div className="matchus-profile-tab-content">
            {activeTab === 'about' && (
              <div className="matchus-profile-details-grid">
                {/* Bio Section */}
                {profile.bio && (
                  <div className="matchus-profile-detail-card">
                    <div className="matchus-detail-card-header">
                      <FaInfoCircle className="matchus-card-icon text-blue-500" />
                      <h3 className="matchus-card-title">About</h3>
                    </div>
                    <div className="matchus-detail-card-content">
                      <p className="matchus-profile-bio-text">{profile.bio}</p>
                      {/* Debug: Bio text alignment check removed */}
                    </div>
                  </div>
                )}

                {/* Interests Section */}
                {profile.interests && (
                  <div className="matchus-profile-detail-card">
                    <div className="matchus-detail-card-header">
                      <FaHeart className="matchus-card-icon text-red-500" />
                      <h3 className="matchus-card-title">Interests</h3>
                    </div>
                    <div className="matchus-detail-card-content">
                      <div className="matchus-interests-grid">
                        {profile.interests.split(',').map((interest, index) => (
                          <span key={index} className="matchus-interest-tag">
                            {interest.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Social Media Section */}
                {(profile.instagram_username || profile.facebook_username || profile.telegram_username) && (
                  <div className="matchus-profile-detail-card">
                    <div className="matchus-detail-card-header">
                      <FaComments className="matchus-card-icon text-pink-500" />
                      <h3 className="matchus-card-title">Social Media</h3>
                    </div>
                    <div className="matchus-detail-card-content">
                      <div className="matchus-social-media-grid">
                        {profile.instagram_username && (
                          <a
                            href={`https://instagram.com/${profile.instagram_username}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="matchus-social-link instagram"
                          >
                            <FaInstagram className="matchus-social-icon" />
                            <span className="matchus-social-text">@{profile.instagram_username}</span>
                          </a>
                        )}
                        {profile.facebook_username && (
                          <a
                            href={`https://facebook.com/${profile.facebook_username}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="matchus-social-link facebook"
                          >
                            <FaFacebook className="matchus-social-icon" />
                            <span className="matchus-social-text">@{profile.facebook_username}</span>
                          </a>
                        )}
                        {profile.telegram_username && (
                          <a
                            href={`https://t.me/${profile.telegram_username}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="matchus-social-link telegram"
                          >
                            <FaTelegram className="matchus-social-icon" />
                            <span className="matchus-social-text">@{profile.telegram_username}</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Personal Info Section */}
                <div className="matchus-profile-detail-card">
                  <div className="matchus-detail-card-header">
                    <FaUser className="matchus-card-icon text-blue-500" />
                    <h3 className="matchus-card-title">Preferences & Personal Info</h3>
                  </div>
                  <div className="matchus-detail-card-content">
                    <div className="matchus-personal-info-grid">
                      {profile.gender && (
                        <div className="matchus-info-item">
                          <span className="matchus-info-label">Gender:</span>
                          <span className="matchus-info-value">{profile.gender}</span>
                        </div>
                      )}
                      {profile.age && (
                        <div className="matchus-info-item">
                          <span className="matchus-info-label">Age:</span>
                          <span className="matchus-info-value">{profile.age}</span>
                        </div>
                      )}
                      {profile.height && (
                        <div className="matchus-info-item">
                          <span className="matchus-info-label">Height:</span>
                          <span className="matchus-info-value">{profile.height} cm</span>
                        </div>
                      )}
                      {profile.weight && (
                        <div className="matchus-info-item">
                          <span className="matchus-info-label">Weight:</span>
                          <span className="matchus-info-value">{profile.weight} kg</span>
                        </div>
                      )}
                      {profile.language && (
                        <div className="matchus-info-item">
                          <span className="matchus-info-label">Language:</span>
                          <span className="matchus-info-value">{profile.language}</span>
                        </div>
                      )}
                      {profile.sexual_preference && (
                        <div className="matchus-info-item">
                          <span className="matchus-info-label">Sexual Interest:</span>
                          <span className="matchus-info-value">
                            {profile.sexual_preference === 1 ? 'Men' :
                             profile.sexual_preference === 2 ? 'Women' :
                             profile.sexual_preference === 3 ? 'Everyone' : 'Not specified'}
                          </span>
                        </div>
                      )}
                      {profile.sexual_orientation && (
                        <div className="matchus-info-item">
                          <span className="matchus-info-label">Orientation:</span>
                          <span className="matchus-info-value">
                            {profile.sexual_orientation === 1 ? 'Straight' :
                             profile.sexual_orientation === 2 ? 'Gay' :
                             profile.sexual_orientation === 3 ? 'Bisexual' : 'Not specified'}
                          </span>
                        </div>
                      )}
                      {profile.interested_in && (
                        <div className="matchus-info-item">
                          <span className="matchus-info-label">Looking for:</span>
                          <span className="matchus-info-value">{profile.interested_in}</span>
                        </div>
                      )}
                      {profile.registered && (
                        <div className="matchus-info-item">
                          <span className="matchus-info-label">Member since:</span>
                          <span className="matchus-info-value">{new Date(profile.registered * 1000).toLocaleDateString()}</span>
                        </div>
                      )}
                      {profile.last_login && (
                        <div className="matchus-info-item">
                          <span className="matchus-info-label">Last login:</span>
                          <span className="matchus-info-value">{new Date(profile.last_login * 1000).toLocaleDateString()}</span>
                        </div>
                      )}
                      
                    </div>
                  </div>
                </div>

              </div>
            )}

            {activeTab === 'photos' && (normalPhotos.length > 0 || privatePhotos.length > 0) && (
              <div className="matchus-profile-detail-card matchus-photos-card">
                <div className="matchus-detail-card-header">
                  <FaImages className="matchus-card-icon text-green-500" />
                  <h3 className="matchus-card-title">
                    Photos
                  </h3>
                </div>
                <div className="matchus-detail-card-content">
                  {/* Normal Photos */}
                  {normalPhotos.length > 0 && (
                    <div className="matchus-photo-section">
                      <h4 className="matchus-photo-section-title">Public Photos ({normalPhotos.length})</h4>
                      <div className="matchus-profile-photos-grid">
                        {normalPhotos.map((photo, index) => (
                          <div
                            key={`normal-${index}`}
                            className={`matchus-photo-item-enhanced ${index === currentPhotoIndex ? 'active' : ''} ${photo.approval_status === 'pending' ? 'pending-approval' : ''}`}
                            onClick={() => photo.approval_status !== 'pending' && openPublicPhotoModal(photo)}
                          >
                            <img
                              src={getUploadUrl(photo.path.startsWith('user-') ? photo.path : `user-${profile.id}/${photo.path}`)}
                              alt={`Public Photo ${index + 1}`}
                            />
                            {photo.approval_status === 'pending' && (
                              <div className="photo-pending-overlay">
                                <div className="pending-text">
                                  <div>Onay Bekliyor</div>
                                  <div className="pending-subtitle">Admin incelemesi gerekli</div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Private Photos */}
                  {privatePhotos.length > 0 && (
                    <div className="matchus-photo-section">
                      <div className="matchus-private-section-header">
                        <h4 className="matchus-photo-section-title">Private Photos ({privatePhotos.length})</h4>
                        <button
                          className="matchus-request-all-access-btn"
                          onClick={() => {
                            // Check if we have granted access to others (from localStorage)
                            const approvedRequests = JSON.parse(localStorage.getItem('approvedPhotoRequests') || '{}');
                            const hasGrantedAccess = approvedRequests[parseInt(id)] || approvedRequests[id];

                            if (hasGrantedAccess) {
                              handleRevokeAccess();
                            } else {
                              handleSendMessageRequest('all');
                            }
                          }}
                          disabled={Object.values(photoAccessRequestsStatus).some(status => status === 'pending') || requestLoading}
                        >
                          {(() => {
                            // Check if we are the profile owner AND have granted access to others (from localStorage)
                            const isProfileOwner = user?.id === parseInt(id);
                            const approvedRequests = JSON.parse(localStorage.getItem('approvedPhotoRequests') || '{}');
                            const hasGrantedAccess = approvedRequests[parseInt(id)] || approvedRequests[id];

                            if (isProfileOwner && hasGrantedAccess) {
                              return (
                            <>
                              <FaTimes className="mr-2" />
                              Revoke Access
                            </>
                              );
                            } else if (Object.values(photoAccessRequestsStatus).some(status => status === 'pending')) {
                              return (
                            <>
                              <FaClock className="mr-2" />
                              Request Pending
                            </>
                              );
                            } else if (photoAccessRequests['all'] === 'message_sent') {
                              return (
                            <>
                              <FaComments className="mr-2" />
                              Message Sent
                            </>
                              );
                            } else {
                              return (
                            <>
                              <FaComments className="mr-2" />
                              Send Access Request to View
                            </>
                              );
                            }
                          })()}
                        </button>
                      </div>
                      <div className="matchus-profile-photos-grid">
                        {privatePhotos.map((photo, index) => {
                          const hasAccess = photoAccessRequestsStatus[photo.id] === 'approved';

                          return (
                            <div
                              key={`private-${index}`}
                              className={`matchus-photo-item-enhanced ${!hasAccess ? 'matchus-private-photo-disabled' : ''} ${photo.approval_status === 'pending' ? 'pending-approval' : ''}`}
                              onClick={hasAccess && photo.approval_status !== 'pending' ? () => openPublicPhotoModal(photo) : undefined}
                            >
                              {/* Normal arka plan resmi */}
                              <img
                                src={getUploadUrl(photo.path.startsWith('user-') ? photo.path : `user-${profile.id}/${photo.path}`)}
                                alt={`Private Photo ${index + 1}`}
                                className={hasAccess ? '' : 'matchus-private-photo-hidden'}
                              />
                              {/* Approval Status Overlay - takes precedence */}
                              {photo.approval_status === 'pending' && (
                                <div className="photo-pending-overlay">
                                  <div className="pending-text">
                                    <div>Onay Bekliyor</div>
                                    <div className="pending-subtitle">Admin incelemesi gerekli</div>
                                  </div>
                                </div>
                              )}
                              {/* Özel overlay - sadece erişim yoksa göster */}
                              {!hasAccess && (
                                <div className="matchus-private-overlay-enhanced">
                                  <div className="matchus-lock-icon">
                                    <FaLock className="text-pink-300 text-2xl" />
                                  </div>
                                  <div className="matchus-private-text">
                                    Private Photo
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'gifts' && (
              <div className="matchus-profile-detail-card">
                <div className="matchus-detail-card-header">
                  <FaGift className="matchus-card-icon text-pink-500" />
                  <h3 className="matchus-card-title">Received Gifts ({gifts.length})</h3>
                </div>
                <div className="matchus-detail-card-content">
                  {gifts.length > 0 ? (
                    <div className="matchus-gifts-grid">
                      {gifts.map((gift, index) => {
                        return (
                        <div key={index} className="matchus-gift-item-received">
                          <div className="matchus-gift-emoji-large">
                            {getGiftEmoji(gift.gift_id, gift.gift_name) || '🎁'}
                          </div>
                          <div className="matchus-gift-info">
                            <span className="matchus-gift-from">From: {gift.sender_name}</span>
                            <span className="matchus-gift-time">{gift.time ? formatDate(gift.time, 'dd-mm-yyyy') : 'Unknown time'}</span>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="matchus-empty-state">
                      <FaGift className="matchus-empty-icon" />
                      <p>No gifts received yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'friends' && (
              <div className="matchus-profile-detail-card">
                <div className="matchus-detail-card-header">
                  <FaHeart className="matchus-card-icon text-pink-500" />
                  <h3 className="matchus-card-title">Friends ({profileFriends.length})</h3>
                </div>
                <div className="matchus-detail-card-content">
                  {profileFriends.length > 0 ? (
                    <div className="matchus-friends-grid">
                      {profileFriends.map((friend, index) => (
                        <Link
                          key={index}
                          to={`/user/profile/${friend.id}`}
                          className="matchus-friend-item hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <div className="relative">
                            <img
                              src={getProfilePicture(friend)}
                              alt={friend.full_name}
                              className="matchus-friend-avatar"
                            />
                            {isOnline(friend.last_active) && (
                              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
                            )}
                          </div>
                          <div className="matchus-friend-info">
                            <span className="matchus-friend-name">{friend.full_name}</span>
                            <span className="matchus-friend-location">
                              <FaMapMarkerAlt className="mr-1" />
                              {friend.city}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="matchus-empty-state">
                      <FaHeart className="matchus-empty-icon" />
                      <p>No friends yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Gift Modal */}
      {showGiftModal && (
        <GiftModal
          isOpen={showGiftModal}
          onClose={() => setShowGiftModal(false)}
          profileId={profile.id}
        />
      )}

      {/* Public Photo Modal */}
      {showPublicPhotoModal && selectedPublicPhoto && (
        <div className="matchus-modal-overlay" onClick={() => setShowPublicPhotoModal(false)}>
          <div className="matchus-public-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="matchus-public-modal-header">
              <h3 className="matchus-public-modal-title">Photo</h3>
              <button
                className="matchus-modal-close-btn"
                onClick={() => setShowPublicPhotoModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="matchus-public-modal-body">
              <img
                src={getUploadUrl(selectedPublicPhoto.path.startsWith('user-') ? selectedPublicPhoto.path : `user-${profile.id}/${selectedPublicPhoto.path}`)}
                alt="Public Photo"
                className="matchus-public-modal-img"
              />
            </div>
          </div>
        </div>
      )}

      {/* Private Photo Access Modal */}
      {showPrivatePhotoModal && selectedPrivatePhoto && (
        <div className="matchus-modal-overlay" onClick={() => setShowPrivatePhotoModal(false)}>
          <div className="matchus-private-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="matchus-private-modal-header">
              <h3 className="matchus-private-modal-title">Private Photo Access</h3>
              <button
                className="matchus-modal-close-btn"
                onClick={() => setShowPrivatePhotoModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="matchus-private-modal-body">
              <div className="matchus-private-modal-image">
                <img
                  src={getUploadUrl(selectedPrivatePhoto.path.startsWith('user-') ? selectedPrivatePhoto.path : `user-${profile.id}/${selectedPrivatePhoto.path}`)}
                  alt="Private Photo"
                  className="matchus-private-modal-img"
                />
              </div>
              <div className="matchus-private-modal-info">
                <p className="matchus-private-modal-text">
                  This photo is private. Send an access request to view it.
                </p>
                <button
                  className="matchus-send-request-btn"
                  onClick={() => handlePrivatePhotoRequest(selectedPrivatePhoto.id)}
                  disabled={photoAccessRequests[selectedPrivatePhoto.id] === 'pending'}
                >
                  {photoAccessRequests[selectedPrivatePhoto.id] === 'pending' ? (
                    <>
                      <FaCheckCircle className="mr-2" />
                      Request Sent
                    </>
                  ) : (
                    <>
                      <FaLock className="mr-2" />
                      Send Access Request
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// CSS Styles for photo approval overlay
const photoApprovalStyles = `
  .pending-approval {
    position: relative;
    cursor: not-allowed !important;
  }

  .photo-pending-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }

  .pending-text {
    text-align: center;
    color: white;
    font-weight: 500;
  }

  .pending-text > div:first-child {
    font-size: 14px;
    margin-bottom: 4px;
  }

  .pending-subtitle {
    font-size: 12px;
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    .pending-text > div:first-child {
      font-size: 12px;
    }

    .pending-subtitle {
      font-size: 10px;
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = photoApprovalStyles;
  document.head.appendChild(styleSheet);
}

export default ProfileDetail;

